package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.LegalDocuments
import com.evoapps.evoshift.domain.model.Owner
import com.evoapps.evoshift.domain.model.SubscriptionStatus
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.OwnerRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.util.AppClock.now

/**
 * Opens a workplace for [ownerId] (creating the owner document the first time), links it to the
 * owner and gives the creator an OWNER engagement. Every new workplace starts with one
 * availability slot covering the whole day (SH-60), so employees can submit availability before
 * the owner defines their own slots; [allDaySlotLabel] is its name in the owner's language (from
 * the UI, like the starter shift names), stored as data so it can be renamed like any slot and
 * reads the same in exports made outside the app.
 */
class CreateWorkplaceUseCase(
    private val workplaceRepository: WorkplaceRepository,
    private val userRepository: UserRepository,
    private val ownerRepository: OwnerRepository
) {
    suspend operator fun invoke(
        ownerId: String,
        workplaceName: String,
        businessName: String? = null,
        allDaySlotLabel: String
    ): Result<String> {
        return try {
            // 1. Check if owner exists, create if not
            val existingOwner = ownerRepository.getOwner(ownerId).getOrThrow()
            if (existingOwner == null) {
                if (businessName.isNullOrBlank()) {
                    return Result.failure(Exception("Business name is required for first-time workplace creation."))
                }
                val newOwner = Owner(
                    uid = ownerId,
                    businessName = businessName,
                    subscriptionStatus = SubscriptionStatus.TRIAL,
                    workplaceIds = emptyList()
                )
                ownerRepository.createOwner(newOwner).getOrThrow()
            }

            // Opening a workplace accepts the terms' data processing annex (the screen says so):
            // kept as evidence of the Art. 28 agreement. Not fatal: the workplace matters more.
            ownerRepository.recordDpaAcceptance(ownerId, LegalDocuments.DPA_VERSION)

            // 2. Create workplace
            val workplaceIdResult = workplaceRepository.createWorkplace(
                ownerId,
                workplaceName,
                availabilitySlots = listOf(AvailabilitySlot.allDay(allDaySlotLabel))
            )
            val workplaceId = workplaceIdResult.getOrThrow()

            // 3. Link workplace to owner
            ownerRepository.addWorkplaceToOwner(ownerId, workplaceId).getOrThrow()

            val today = now().toString().substring(0, 10)

            // 4. Create Engagement for user
            val ownerEngagement = Engagement(
                workplaceId = workplaceId,
                role = "Owner",
                skills = emptyList(),
                permission = UserPermission.OWNER,
                isActive = true,
                startDate = today,
                endDate = null,
                isPrimary = true,
                cachedWorkplaceName = workplaceName
            )

            userRepository.addEngagement(ownerId, ownerEngagement).getOrThrow()

            Result.success(workplaceId)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}